import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import * as yaml from 'js-yaml';
import { usePinnedItems, PinnedItem } from '../../contexts/PinnedItemsContext';
import { useFavorites } from '../../contexts/FavoritesContext';
import { useAuth } from '../../contexts/AuthContext';
import { generateItemId } from '../../utils/navigationHelpers';
import { getIconComponent } from '../../utils/navIcons';
import PinButton from './PinButton';
import FavoriteButton from './FavoriteButton';

interface MoreItemsFlyoutProps {
  open: boolean;
  onClose: () => void;
}

// Interface for navigation section from YAML
interface NavigationSection {
  id: string;
  name: string;
  icon: string;
  route?: string;
  items?: NavigationItem[];
}

interface NavigationItem {
  id: string;
  name: string;
  route: string;
  icon?: string;
  visible_roles?: string[];
  // When true, teachers with is_consultant=true ALSO see the item, in
  // addition to whatever visible_roles allows. (Consultant is currently a
  // flag on Teacher, not a UserRole, so visible_roles alone can't reach it.)
  consultant_visible?: boolean;
}

const MoreItemsFlyout: React.FC<MoreItemsFlyoutProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { pinItem, unpinItem, isPinned, setTemporaryItem } = usePinnedItems();
  const { addFavorite, removeFavorite, isFavorite } = useFavorites();
  const { user, isAdmin, isSchoolAdmin, isConsultant } = useAuth();
  const [sections, setSections] = useState<NavigationSection[]>([]);
  const [loading, setLoading] = useState(false);

  // Load navigation from YAML
  useEffect(() => {
    const loadNavigation = async () => {
      if (!open) return; // Only load when flyout is opened

      setLoading(true);
      try {
        const response = await fetch('/navigation/caliper-navigation.yaml');
        const yamlText = await response.text();
        const config: any = yaml.load(yamlText);

        if (config && config.sidebar && config.sidebar.sections) {
          setSections(config.sidebar.sections);
        }
      } catch (error) {
        console.error('Error loading navigation:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNavigation();
  }, [open]);

  const handleItemClick = (item: NavigationItem, sectionName: string) => {
    const itemId = generateItemId(item.route, item.name);

    // If the item is already pinned, just navigate
    if (isPinned(itemId)) {
      navigate(item.route);
      onClose();
      return;
    }

    // Otherwise, create a temporary item
    const tempItem: PinnedItem = {
      item_id: itemId,
      name: item.name,
      route: item.route,
      icon: item.icon || '',
      section: sectionName,
      category: '',
    };

    setTemporaryItem(tempItem);
    navigate(item.route);
    onClose();
  };

  const handlePinToggle = async (item: NavigationItem, sectionName: string) => {
    const itemId = generateItemId(item.route, item.name);

    try {
      if (isPinned(itemId)) {
        await unpinItem(itemId);
      } else {
        const pinnedItem: PinnedItem = {
          item_id: itemId,
          name: item.name,
          route: item.route,
          icon: item.icon || '',
          section: sectionName,
          category: '',
        };
        await pinItem(pinnedItem);
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const handleFavoriteToggle = async (item: NavigationItem, sectionName: string) => {
    const itemId = generateItemId(item.route, item.name);

    try {
      if (isFavorite(itemId)) {
        await removeFavorite(itemId);
      } else {
        const favoriteItem = {
          id: itemId,  // This becomes item_id in the API payload
          name: item.name,
          route: item.route,
          icon: item.icon || '',
          section: sectionName,
          category: '',
        };
        await addFavorite(favoriteItem);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Determine the current user's roles for filtering
  const userRole = user?.user_profile?.role;
  const userRoles: string[] = [];
  if (userRole) userRoles.push(userRole);
  if (isAdmin && !userRoles.includes('admin')) userRoles.push('admin');
  if (isSchoolAdmin && !userRoles.includes('school_admin')) userRoles.push('school_admin');

  const isItemVisible = (item: NavigationItem): boolean => {
    // Consultant flag bypasses the role gate when consultant_visible is set on
    // the YAML entry. (Subject management is the canonical case today.)
    if (item.consultant_visible && isConsultant) return true;
    if (!item.visible_roles || item.visible_roles.length === 0) return true;
    return item.visible_roles.some(role => userRoles.includes(role));
  };

  // Filter sections to only include visible items, and hide empty sections
  const visibleSections = sections
    .map(section => ({
      ...section,
      items: section.items?.filter(isItemVisible),
    }))
    .filter(section => !section.items || section.items.length > 0);

  // Flatten all visible items from all sections
  const allItems: Array<{ item: NavigationItem; sectionName: string }> = [];
  visibleSections.forEach(section => {
    if (section.items) {
      section.items.forEach(item => {
        allItems.push({ item, sectionName: section.name });
      });
    }
  });

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450, md: 500 },
          maxWidth: '100vw',
          left: '240px', // Position next to navbar (navbar width = 240px)
          top: '64px', // Position below the AppBar
          height: 'calc(100% - 64px)', // Full height minus AppBar
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoreHorizIcon />
          <Box>
            <Typography variant="h6">More Items</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Browse and organize your navigation with favorites ⭐ and pins 📍
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>


      {/* Content */}
      <Box sx={{ p: 3, overflow: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && visibleSections.length === 0 && (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ p: 4 }}>
            No navigation items found
          </Typography>
        )}

        {!loading && visibleSections.length > 0 && (
          <>
            {visibleSections.map(section => (
              <Box key={section.id} sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}
                >
                  {section.name}
                </Typography>

                {section.items && section.items.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    {section.items.map(item => {
                      const itemId = generateItemId(item.route, item.name);
                      const isItemPinned = isPinned(itemId);
                      const isItemFavorited = isFavorite(itemId);
                      return (
                        <Box key={item.id}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              border: isItemPinned ? '2px solid' : '1px solid',
                              borderColor: isItemPinned ? 'primary.main' : 'divider',
                              bgcolor: isItemPinned ? 'primary.50' : 'background.paper',
                              '&:hover': {
                                boxShadow: 4,
                                transform: 'translateY(-2px)',
                              },
                            }}
                            onClick={() => handleItemClick(item, section.name)}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, display: 'flex', gap: 1.5 }}>
                                  <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                                    {getIconComponent(item.icon)}
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {item.name}
                                      </Typography>
                                      {isItemPinned && (
                                        <Chip
                                          label="Pinned"
                                          size="small"
                                          color="primary"
                                          sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                      )}
                                      {isItemFavorited && (
                                        <Chip
                                          label="Favorited"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            bgcolor: '#ffd700',
                                            color: '#000',
                                            fontWeight: 600
                                          }}
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                      {section.name}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 0.5 }}>
                                  <FavoriteButton
                                    isFavorited={isItemFavorited}
                                    onToggleFavorite={() => handleFavoriteToggle(item, section.name)}
                                    size="small"
                                  />
                                  <PinButton
                                    isPinned={isItemPinned}
                                    onTogglePin={() => handlePinToggle(item, section.name)}
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    No items in this section
                  </Typography>
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default MoreItemsFlyout;
